const DATA_TYPE_PATTERNS = [
  { pattern: /_amount$|_value$|_impact$/, type: 'Decimal' },
  { pattern: /_date$/, type: 'Date' },
  { pattern: /^is_|_flag$/, type: 'Boolean' },
];

export const DATA_CONFIGURATION_DATA_TYPES = ['String', 'Decimal', 'Date', 'Boolean'];

export const COLUMN_MASK = {
  NONE: 'none',
  HASH: 'hash',
};

export const COLUMN_MASK_ITEMS = [
  { value: COLUMN_MASK.NONE, label: 'None' },
  { value: COLUMN_MASK.HASH, label: 'Hash' },
];

export function normalizeColumnMask(value) {
  return String(value || '').toLowerCase() === COLUMN_MASK.HASH
    ? COLUMN_MASK.HASH
    : COLUMN_MASK.NONE;
}

export function columnMaskLabel(value) {
  return normalizeColumnMask(value) === COLUMN_MASK.HASH ? 'Hash' : 'None';
}

export function inferDataType(columnName = '', fallback = 'String') {
  const name = String(columnName || '').trim();
  if (!name) return fallback;
  const match = DATA_TYPE_PATTERNS.find(({ pattern }) => pattern.test(name));
  return match?.type || fallback;
}

export function slugifyColumnName(name = '') {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

export function normalizeDropdownValue(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    return String(value.value || value.label || '').trim();
  }
  return String(value).trim();
}

export function cloneMappingRows(rows = []) {
  return (rows || []).map((row) => ({ ...row }));
}

export function buildErpColumnItems(sourceHeaders = [], headerLabels = {}, mappedValues = []) {
  const options = new Set(
    (sourceHeaders || []).filter(Boolean).concat((mappedValues || []).filter(Boolean))
  );
  return Array.from(options)
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }))
    .map((value) => ({
      value,
      label: headerLabels[value] ? `${headerLabels[value]} (${value})` : value,
    }));
}

export function getUnmappedErpColumnItems(erpColumnItems = [], mappingRows = []) {
  const mapped = new Set(
    (mappingRows || []).map((row) => row.source).filter(Boolean)
  );
  return (erpColumnItems || []).filter((item) => !mapped.has(item.value));
}

export function getErpItemsForRow(erpColumnItems = [], mappingRows = [], row = {}) {
  const mappedByOthers = new Set(
    (mappingRows || [])
      .filter((item) => item !== row && item.source)
      .map((item) => item.source)
  );
  return (erpColumnItems || []).filter(
    (item) => !mappedByOthers.has(item.value) || item.value === row.source
  );
}

const AMOUNT_PARTS = ['amount', 'value', 'impact', 'total', 'balance'];

export function scoreErpColumnMatch(destination = '', erpColumn = '', searchQuery = '') {
  const dest = String(destination || '').toLowerCase();
  const erp = String(erpColumn || '').toLowerCase();
  const query = String(searchQuery || '').trim().toLowerCase();

  if (!erp) return 0;

  let score = 0;
  const destParts = dest.split('_').filter(Boolean);
  const erpParts = erp.split('_').filter(Boolean);
  const destIsAmount = destParts.some((part) => AMOUNT_PARTS.includes(part));
  const erpIsAmount = erpParts.some((part) => AMOUNT_PARTS.includes(part));
  const destIsDate = destParts.includes('date');
  const erpIsDate = erpParts.includes('date');

  if (dest) {
    if (dest === erp) score = Math.max(score, 100);
    else if (erp.includes(dest) || dest.includes(erp)) score = Math.max(score, 88);
    else {
      const sharedParts = destParts.filter((part) => erp.includes(part));
      if (sharedParts.length) {
        score = Math.max(score, Math.min(95, 52 + sharedParts.length * 18));
      }
    }

    if (destIsAmount && erpIsAmount) {
      const sharedAmount = destParts.filter((part) => erpParts.includes(part));
      score = Math.max(score, sharedAmount.length ? 94 : 86);
    }
    if (dest.includes('invoice') && destIsAmount && erp === 'total') {
      score = Math.max(score, 98);
    }
    if ((dest.includes('balance') || dest.includes('impact')) && erp === 'balance') {
      score = Math.max(score, 98);
    }
    if (destIsDate && erpIsDate) {
      score = Math.max(score, 90);
    }
    if (dest === erp) {
      score = 100;
    } else if (destIsAmount && !erpIsAmount) {
      score = Math.min(score, 40);
    } else if (destIsDate && !erpIsDate) {
      score = Math.min(score, 40);
    }
  }

  if (query) {
    if (erp.includes(query)) {
      score = Math.max(score, erp === query ? 100 : 92);
    } else if (query.includes('account') && erp.includes('amount')) {
      score = Math.max(score, erp.includes('total_invoice') ? 68 : 62);
    } else if (dest && erp.includes(query)) {
      score = Math.max(score, 75);
    }
  }

  return Math.min(100, score);
}

export function matchesAllColumnsSearch(erpColumn = '', searchQuery = '') {
  const erp = String(erpColumn || '').toLowerCase();
  const query = String(searchQuery || '').trim().toLowerCase();
  if (!query) return true;
  if (erp.includes(query)) return true;
  if (query.includes('account') && erp.includes('amount')) return true;
  return false;
}

export function buildErpDropdownSections({
  destination = '',
  items = [],
  suggestedSource = '',
  searchQuery = '',
  selectedValue = '',
} = {}) {
  const scored = (items || [])
    .map((item) => {
      let matchScore = scoreErpColumnMatch(destination, item.value, searchQuery);
      if (suggestedSource && item.value === suggestedSource) {
        matchScore = Math.max(matchScore, 92);
      }
      return { ...item, matchScore };
    })
    .sort((a, b) => b.matchScore - a.matchScore);

  const aiSuggestions = scored.filter((item) => item.matchScore >= 50).slice(0, 2);
  const aiValues = new Set(aiSuggestions.map((item) => item.value));
  const allColumns = (items || [])
    .filter((item) => !aiValues.has(item.value))
    .filter((item) => matchesAllColumnsSearch(item.value, searchQuery))
    .sort((a, b) =>
      String(a.value).localeCompare(String(b.value), undefined, { sensitivity: 'base' })
    );

  return {
    aiSuggestions,
    allColumns,
    selectedValue,
  };
}
